import React, { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "../../../shared/utils/cn";

interface Props {
  startDate: string; // YYYY-MM-DD, 빈 값이면 미선택
  endDate: string;
  onChange: (startDate: string, endDate: string) => void;
  placeholder?: string;
  className?: string;
}

const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

const pad = (value: number) => String(value).padStart(2, "0");
const toKey = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const fromKey = (key: string) => {
  const [year, month, day] = key.split("-").map(Number);
  return new Date(year, month - 1, day);
};
const addDays = (date: Date, days: number) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
const formatLabel = (key: string) => key.split("-").join(".");

/** 주문 기간처럼 시작일~종료일을 고르는 달력. 두 번째 날짜를 고르는 순간 적용된다. */
export const DateRangePicker: React.FC<Props> = ({
  startDate,
  endDate,
  onChange,
  placeholder = "기간 선택",
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pendingStart, setPendingStart] = useState<string | null>(null);
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const [viewMonth, setViewMonth] = useState(() => {
    const base = startDate ? fromKey(startDate) : new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const todayKey = toKey(new Date());
  const hasValue = !!startDate || !!endDate;

  // 열 때마다 선택된 시작일의 달을 보여주고, 반쯤 고른 상태는 버린다.
  useEffect(() => {
    if (!open) return;
    const base = startDate ? fromKey(startDate) : new Date();
    setViewMonth(new Date(base.getFullYear(), base.getMonth(), 1));
    setPendingStart(null);
    setHoverKey(null);
  }, [open, startDate]);

  useEffect(() => {
    if (!open) return;
    const handlePointer = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const days = useMemo(() => {
    const first = addDays(viewMonth, -viewMonth.getDay());
    return Array.from({ length: 42 }, (_, index) => addDays(first, index));
  }, [viewMonth]);

  // 시작일만 고른 상태에서는 마우스 위치까지를 범위로 미리 보여준다.
  const [rangeStart, rangeEnd] = useMemo(() => {
    if (pendingStart) {
      const other = hoverKey ?? pendingStart;
      return pendingStart <= other ? [pendingStart, other] : [other, pendingStart];
    }
    return [startDate || null, endDate || startDate || null];
  }, [pendingStart, hoverKey, startDate, endDate]);

  const commit = (from: string, to: string) => {
    onChange(from, to);
    setOpen(false);
  };

  const handleDayClick = (key: string) => {
    if (!pendingStart) {
      setPendingStart(key);
      return;
    }
    commit(pendingStart <= key ? pendingStart : key, pendingStart <= key ? key : pendingStart);
  };

  const today = new Date();
  const presets = [
    { label: "오늘", range: () => [todayKey, todayKey] },
    { label: "최근 7일", range: () => [toKey(addDays(today, -6)), todayKey] },
    { label: "이번 달", range: () => [toKey(new Date(today.getFullYear(), today.getMonth(), 1)), todayKey] },
    {
      label: "지난 달",
      range: () => [
        toKey(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        toKey(new Date(today.getFullYear(), today.getMonth(), 0)),
      ],
    },
  ];

  const triggerLabel = hasValue
    ? `${formatLabel(startDate || endDate)} ~ ${formatLabel(endDate || startDate)}`
    : placeholder;

  return (
    <div ref={containerRef} className={cn("relative h-10", className)}>
      <button
        type="button"
        aria-label="주문 기간 선택"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
        className={cn(
          "w-full h-full flex items-center gap-2 bg-white pl-3 pr-9 rounded-[8px] border text-sm text-left transition-colors focus:outline-none",
          open ? "border-[#4285F4]" : "border-[#e0e0e0] hover:border-[#4285F4]",
        )}
      >
        <img className="w-[1rem] flex-shrink-0" alt="" src="/assets/icons/Calendar.svg" />
        <span className={cn("truncate font-medium", hasValue ? "text-[#222222]" : "text-[#BBBBBB]")}>
          {triggerLabel}
        </span>
      </button>
      {hasValue ? (
        <button
          type="button"
          aria-label="주문 기간 해제"
          onClick={() => onChange("", "")}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full hover:bg-[#f5f5f5]"
        >
          <img className="w-[0.9rem] opacity-60" alt="" src="/assets/icons/Close Square.svg" />
        </button>
      ) : (
        <img
          className="absolute right-3 top-1/2 -translate-y-1/2 w-[0.6837rem] pointer-events-none"
          alt=""
          src="/assets/icons/Arrow - Down 2.svg"
        />
      )}

      {open && (
        <div
          role="dialog"
          aria-label="주문 기간 달력"
          className="absolute z-30 mt-2 left-0 md:left-auto md:right-0 w-[19.5rem] max-w-[calc(100vw-2.5rem)] bg-white border border-[#e0e0e0] rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,0.08)] p-4"
        >
          <div className="flex flex-wrap gap-1.5 mb-4">
            {presets.map(preset => (
              <button
                key={preset.label}
                type="button"
                onClick={() => {
                  const [from, to] = preset.range();
                  commit(from, to);
                }}
                className="px-2.5 py-1 text-xs rounded-full border border-[#e0e0e0] text-[#555555] hover:border-[#4285F4] hover:text-[#4285F4] transition-colors"
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              aria-label="이전 달"
              onClick={() => setViewMonth(month => new Date(month.getFullYear(), month.getMonth() - 1, 1))}
              className="w-8 h-8 flex items-center justify-center rounded-[8px] hover:bg-[#F5F8FF]"
            >
              <img className="w-4" alt="" src="/assets/icons/Arrow - Left 2.svg" />
            </button>
            <span className="text-[0.9375rem] font-semibold">
              {viewMonth.getFullYear()}년 {viewMonth.getMonth() + 1}월
            </span>
            <button
              type="button"
              aria-label="다음 달"
              onClick={() => setViewMonth(month => new Date(month.getFullYear(), month.getMonth() + 1, 1))}
              className="w-8 h-8 flex items-center justify-center rounded-[8px] hover:bg-[#F5F8FF]"
            >
              <img className="w-4" alt="" src="/assets/icons/Arrow - Right 2.svg" />
            </button>
          </div>

          <div className="grid grid-cols-7 mb-1">
            {WEEKDAYS.map((label, index) => (
              <span
                key={label}
                className={cn(
                  "h-7 flex items-center justify-center text-xs font-medium",
                  index === 0 ? "text-[#F24A4A]" : index === 6 ? "text-[#4285F4]" : "text-[#888888]",
                )}
              >
                {label}
              </span>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-y-1" onMouseLeave={() => setHoverKey(null)}>
            {days.map(day => {
              const key = toKey(day);
              const inMonth = day.getMonth() === viewMonth.getMonth();
              const isStart = key === rangeStart;
              const isEnd = key === rangeEnd;
              const isEdge = isStart || isEnd;
              const inRange = !!rangeStart && !!rangeEnd && key > rangeStart && key < rangeEnd;
              const hasSpan = !!rangeStart && !!rangeEnd && rangeStart !== rangeEnd;
              return (
                <div
                  key={key}
                  className={cn(
                    "h-9 flex items-center justify-center",
                    inRange && "bg-[#E8EFFF]",
                    hasSpan && isStart && "bg-gradient-to-r from-transparent from-50% to-[#E8EFFF] to-50%",
                    hasSpan && isEnd && "bg-gradient-to-l from-transparent from-50% to-[#E8EFFF] to-50%",
                  )}
                >
                  <button
                    type="button"
                    aria-label={key}
                    aria-pressed={isEdge}
                    onClick={() => handleDayClick(key)}
                    onMouseEnter={() => pendingStart && setHoverKey(key)}
                    className={cn(
                      "w-9 h-9 rounded-full text-sm transition-colors",
                      isEdge
                        ? "bg-[#4285F4] text-white font-semibold"
                        : cn(
                            "hover:bg-[#F5F8FF]",
                            !inMonth && "text-[#cccccc]",
                            inMonth && day.getDay() === 0 && "text-[#F24A4A]",
                            inMonth && day.getDay() === 6 && "text-[#4285F4]",
                            inMonth && day.getDay() !== 0 && day.getDay() !== 6 && "text-[#222222]",
                            key === todayKey && "border border-[#4285F4]",
                          ),
                    )}
                  >
                    {day.getDate()}
                  </button>
                </div>
              );
            })}
          </div>

          <p className="mt-3 pt-3 border-t border-[#f2f2f2] text-xs text-[#757575]">
            {pendingStart
              ? `${formatLabel(pendingStart)} 부터 · 종료일을 선택하세요`
              : "시작일과 종료일을 차례로 선택하세요"}
          </p>
        </div>
      )}
    </div>
  );
};
